// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// A tile's picture, kept between visits (M8-85).
//
// **The render is the expensive thing in the roster, and it was being done again every time.**
// Measured on a desktop GPU: 116 ms a machine before M8-84 trimmed the surfaces nobody sees, and
// still ~90 ms with the furniture in. A phone is several times that. The painter already refuses to
// rasterise the same machine twice *within* a page — that is the claim `rasterisations` counts —
// but a reload threw the lot away, and a roster is a page people come back to.
//
// So the finished picture is kept, as a picture. First visit renders once; every visit after that
// decodes a bitmap, which is the difference between a hundred milliseconds of GPU work and a few
// milliseconds of decode.
//
// WHY NOT A FILE
// --------------
// The obvious shape is a PNG per machine in `public/`, and it cannot work here for three reasons,
// the third of which is the one that settles it. There is no `public/`; `check-assets` fails the
// build on an image in source **or** in `dist/`, and on a `data:` URI inside the bundle; and **a
// player's machine is not in the repository** — it is created in the Builder after the build
// shipped, so a folder of files could only ever cover the machines HCC ships and none of the ones
// anybody makes. A store keyed by content hash covers both with one code path.
//
// A server-side store would go further still — render once for *everyone* rather than once per
// browser — and it is deliberately not this. Nothing in this product can execute the render on a
// server: Edge Functions are Deno at the edge with no GPU and Render hosts a static site, so the
// only thing that could produce the bytes is a client, and a client-supplied image cannot be
// checked against the machine it claims to be a picture of. That is an image-upload surface serving
// arbitrary bytes to every visitor, which is exactly what 04's content rule exists to refuse.
//
// WHAT THE KEY HAS TO SAY
// -----------------------
// `definitionHash` says **which machine**. It cannot say which *picture*, and this project has now
// been caught by that twice in one week — see `net/artProjection.ts`. A stored bitmap is a fact
// about the machine *and* about the code that drew it, so the key carries `TILE_RENDER` and the
// pixel size as well. The size is automatic. The version is not, and the comment on it says so.

import { openTilePictures } from '../storage/machineCache'
import type { DocumentStore } from '../storage/db'

/** What a stored picture holds: where each insert is, in fractions of the frame. */
export interface StoredInsert {
  readonly x: number
  readonly y: number
  readonly r: number
  readonly colour: string
  readonly phase: number
}

export interface StoredPicture {
  readonly image: HTMLImageElement
  readonly inserts: readonly StoredInsert[]
}

/**
 * Which drawing this build makes.
 *
 * **Bump it whenever the picture changes** — the camera, the framing, what is drawn, the lighting.
 * Not the payload: that is `ART_PROJECTION`, and the two move for different reasons. M8-81 through
 * M8-84 each changed this picture under a `definitionHash` that by design never moves, and a
 * stored bitmap keyed on the hash alone would have shown a player the machine as it looked three
 * milestones ago with nothing anywhere to say why.
 *
 * There is no test that can catch a missed bump — a render is a picture, and a picture is not a
 * key set — so this is one of the few places in this repository asking to be remembered rather
 * than checked. The pixel size travels in the key beside it precisely because *that* half can be
 * read off the code, and half a guard is better than none.
 */
export const TILE_RENDER = 1

/**
 * A stable key for a machine with no `definitionHash` to use.
 *
 * **The bundled machine has none, deliberately.** `tileFor` sets it to `undefined` with a comment
 * saying a made-up value there "would be a string that looks like a hash", and that is right: a
 * definition hash is a wire fact, agreed with the server, and inventing one would put a lie in the
 * field an `If-None-Match` is built from. So this is a different field with a different meaning —
 * *which picture is this*, answered locally — and it is named and prefixed so it can never be
 * mistaken for the other.
 *
 * FNV-1a over the document's own bytes: not cryptographic, and it does not need to be. A cache key
 * has one job, which is to change when the content changes, and this runs once at module load over
 * a string the page already holds.
 */
export function contentKey(text: string): string {
  let hash = 0x811c9dc5
  for (let i = 0; i < text.length; i++) {
    hash ^= text.charCodeAt(i)
    // The FNV prime, as shifts: `Math.imul` keeps this in 32 bits, where the algorithm lives.
    hash = Math.imul(hash, 0x01000193) >>> 0
  }
  return `local-${hash.toString(16).padStart(8, '0')}`
}

/** How a machine's picture is filed. Content, drawing and size: all three decide the bytes. */
export function pictureKey(definitionHash: string, width: number, height: number): string {
  return `${TILE_RENDER}/${width}x${height}/${definitionHash}`
}

/**
 * The quality a tile is stored at.
 *
 * WebP at 0.82 puts a 360×380 machine in about 13 KB. Lossless would be four times that for a
 * difference nobody can see at this size, and a lower quality starts to show as mush in the lamp
 * inserts, which are the smallest bright things in the frame.
 */
const FORMAT = 'image/webp'
const QUALITY = 0.82

/** What a stored row holds. Plain JSON, because the document store keeps documents. */
interface PictureRow {
  readonly image: string
  readonly inserts: readonly StoredInsert[]
}

const rowOf = (value: unknown): PictureRow | undefined => {
  if (typeof value !== 'object' || value === null) return undefined
  const row = value as Record<string, unknown>
  if (typeof row['image'] !== 'string' || row['image'] === '') return undefined
  if (!Array.isArray(row['inserts'])) return undefined
  return { image: row['image'], inserts: row['inserts'] as StoredInsert[] }
}

/**
 * One machine's stored picture, decoded and ready to blit, or nothing.
 *
 * **Nothing is an ordinary answer**, not a failure to report: a first visit, a cleared store, a
 * private window, a browser that refuses IndexedDB, a quota that filled up. Every one of them
 * means "render it", which is what the caller did before this existed.
 */
export async function loadPicture(
  definitionHash: string,
  width: number,
  height: number,
  supplied?: DocumentStore,
): Promise<StoredPicture | undefined> {
  try {
    if (typeof globalThis.document === 'undefined' || typeof globalThis.Image === 'undefined') {
      return undefined
    }
    const store = await openTilePictures(supplied)
    const row = rowOf(await store.get(pictureKey(definitionHash, width, height)))
    if (row === undefined) return undefined
    const image = new globalThis.Image()
    image.src = row.image
    // `decode` rather than `onload`, so the bitmap is ready to draw the moment this resolves — an
    // image that has loaded but not decoded still costs its decode on the first `drawImage`, which
    // would be inside the frame loop.
    await image.decode()
    return { image, inserts: row.inserts }
  } catch {
    return undefined
  }
}

/**
 * Keep one machine's picture.
 *
 * Fire and forget: the tile is already on screen by the time this is called, and a store that
 * cannot write — a full quota, a private window — must not turn a drawn tile into a broken one.
 */
export async function savePicture(
  definitionHash: string,
  surface: HTMLCanvasElement,
  inserts: readonly StoredInsert[],
  supplied?: DocumentStore,
): Promise<boolean> {
  try {
    const image = surface.toDataURL(FORMAT, QUALITY)
    // A browser that will not encode WebP answers with a PNG data URL instead, which is larger but
    // correct. One that answers with nothing at all is not worth a row.
    if (!image.startsWith('data:image/')) return false
    const store = await openTilePictures(supplied)
    return await store.put(
      pictureKey(definitionHash, surface.width, surface.height),
      { image, inserts } satisfies PictureRow,
      definitionHash,
      '',
    )
  } catch {
    return false
  }
}
